import { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Box, Text } from '@chakra-ui/react';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1a202c',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      '::placeholder': {
        color: '#a0aec0',
      },
    },
    invalid: {
      color: '#e53e3e',
      iconColor: '#e53e3e',
    },
  },
};

/**
 * Stripe CardElement wrapped in a Chakra-styled container.
 * Exposes getPaymentMethod() via the onReady callback so parents can trigger it.
 */
export default function CardForm({ onReady }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);

  // Expose a function the parent can call to create a PaymentMethod
  const getPaymentMethod = async () => {
    if (!stripe || !elements) {
      throw new Error('Stripe has not loaded yet');
    }

    const cardElement = elements.getElement(CardElement);
    const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    if (stripeError) {
      setError(stripeError.message);
      throw new Error(stripeError.message);
    }

    setError(null);
    return paymentMethod.id;
  };

  // Pass getPaymentMethod up to parent
  if (onReady) {
    onReady(getPaymentMethod);
  }

  return (
    <Box>
      <Box
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        p={3}
        bg="white"
        _hover={{ borderColor: 'gray.300' }}
      >
        <CardElement
          options={CARD_ELEMENT_OPTIONS}
          onChange={(e) => {
            if (e.error) {
              setError(e.error.message);
            } else {
              setError(null);
            }
          }}
        />
      </Box>
      {error && (
        <Text color="red.500" fontSize="sm" mt={1}>
          {error}
        </Text>
      )}
    </Box>
  );
}
